"use client";

import { ArrowsChevronDown } from "@fanduel/formation-react-icons";
import { fanduel as fd } from "@fanduel/formation-tokens";
import cx from "classnames";
import { pickContrastingTeamColors } from "@/utils/colorContrastChecker";
import { ScheduledGameT } from "@/utils/hooks/useScheduledGamesFilter";
import { findGameState, NflGameState } from "./findGameState";
import GameInfo from "./GameInfo/GameInfo";
import GameLines from "./GameLines/GameLines";
import LiveAndPostGameExpansionContent from "./LiveAndPostGameExpansionContent/LiveAndPostGameExpansionContent";
import PreGameExpansionContent from "./PreGameExpansionContent/PreGameExpansionContent";
import Teams from "./Teams/Teams";
import styles from "./nflMatchupCard.module.scss";

interface NflMatchupCardProps {
  game: ScheduledGameT;
  expanded: boolean;
  handleExpand: (index: number) => void;
  index: number;
  isTooSmallForFullNames: boolean;
  isTablet: boolean;
  isMobile: boolean;
}

const buildOddsStrings = (
  game: ScheduledGameT,
  gameState: NflGameState
): { spreadString: string; totalString: string } => {
  const { odds, homeTeam, awayTeam, closingLines } = game;

  const hasCtpSpread = odds?.home?.spread?.value;
  const hasClosingSpread = closingLines?.spread;
  const showClosingLines = !(gameState === "PRE" && hasCtpSpread);
  let homeIsOddsFavored = false;
  if (showClosingLines && closingLines?.spread && closingLines.spread < 0) {
    homeIsOddsFavored = true;
  }
  if (
    !showClosingLines &&
    odds?.home?.spread?.value &&
    Number.parseFloat(odds.home.spread.value) < 0
  ) {
    homeIsOddsFavored = true;
  }
  let spreadString = ""; // default to empty if we don't have the proper spread data and team name.
  if (
    (showClosingLines && hasClosingSpread) ||
    (!showClosingLines && hasCtpSpread)
  ) {
    const teamName = homeIsOddsFavored
      ? homeTeam?.shortName
      : awayTeam?.shortName;
    const spread = showClosingLines
      ? closingLines?.spread
      : Number.parseFloat(odds?.home?.spread?.value || "");
    if (teamName && spread) {
      spreadString = `${teamName} -${Math.abs(spread)}`;
    }
  }
  let totalString = ""; // default to empty if we don't have the proper total data.
  const total = showClosingLines
    ? closingLines?.total
    : odds?.home?.total?.value?.slice(2);
  if (total) {
    totalString = `Total ${total}`;
  }

  return { spreadString, totalString };
};

const NflMatchupCard = ({
  game,
  expanded,
  handleExpand,
  index,
  isTooSmallForFullNames,
  isTablet,
  isMobile,
}: NflMatchupCardProps) => {
  const { homeTeam, awayTeam } = game;
  if (homeTeam == null || awayTeam == null) {
    return null;
  }
  const { homeColor, awayColor } = pickContrastingTeamColors(
    {
      primaryColor: homeTeam?.primaryColor,
      secondaryColor: homeTeam?.secondaryColor,
    },
    {
      primaryColor: awayTeam?.primaryColor,
      secondaryColor: awayTeam?.secondaryColor,
    },
    fd.colors.backgroundBase
  );
  homeTeam.primaryColor = homeColor;
  awayTeam.primaryColor = awayColor;
  const iconSize = 24;
  const gameState: NflGameState = findGameState(game);
  const { spreadString, totalString } = buildOddsStrings(game, gameState);

  return (
    <div className={styles.cardContainer}>
      <GameInfo game={game} isMobile={isMobile} gameState={gameState} />
      <div className={styles.mainAndExpand}>
        <div className={styles.main}>
          <Teams
            homeTeam={homeTeam}
            awayTeam={awayTeam}
            isMobile={isMobile}
            isTooSmallForFullNames={isTooSmallForFullNames}
            isTablet={isTablet}
            gameState={gameState}
            clock={game?.clock ?? ""}
            quarter={game?.quarter ?? ""}
            homeScore={game?.homeTeam?.stats?.points ?? null}
            awayScore={game?.awayTeam?.stats?.points ?? null}
          />
          {spreadString || totalString ? (
            <GameLines
              homeTeamWinProbability={homeTeam?.winProbability}
              spreadString={spreadString}
              totalString={totalString}
              isMobile={isMobile}
              gameState={gameState}
            />
          ) : null}
        </div>
        <div className={styles.expandButton}>
          <button
            data-testid="expand-button"
            onClick={() => handleExpand(index)}
          >
            <ArrowsChevronDown
              className={styles.chevron}
              width={iconSize}
              height={iconSize}
              style={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </button>
        </div>
      </div>
      <div
        className={cx(
          styles.expansionContainer,
          expanded ? styles.expanded : null
        )}
      >
        {gameState === "PRE" && <PreGameExpansionContent game={game} />}
        {(gameState === "LIVE" || gameState === "POST") && (
          <LiveAndPostGameExpansionContent
            game={game}
            isTooSmallForFullNames={isTooSmallForFullNames}
          />
        )}
      </div>
    </div>
  );
};

export default NflMatchupCard;
